import { Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedUnit } from '@/features/project/projectSlice';
import { Process, Unit } from '@urbanmix-tech/shared-js';
import { useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface ProcessRow {
  id: string;
  unit?: Unit;
  process?: Process;
  data: Record<string, any>;
}

type ProcessCardProps = {
  process: Process;
  row: ProcessRow;
  units: Unit[];
  isHovered?: boolean;
  onHoverUnit?: (unitId: string | null) => void;
  onSelectUnit?: (unitId: string | null) => void;
};

export function ProcessCard({
  process,
  row,
  units,
  isHovered = false,
  onHoverUnit,
  onSelectUnit,
}: ProcessCardProps) {
  const dispatch = useDispatch();
  const visualizer = useSelector((state: any) => state.project.projectVisualizer);
  const [hovering, setHovering] = useState(false);

  const daysRunning = row.data['Days Running'].value as number;
  const urgency = daysRunning < 1 ? 'low' : daysRunning < 7 ? 'medium' : daysRunning < 30 ? 'high' : 'critical';
  const borderColor = visualizer?.getColor ? visualizer.getColor(daysRunning) : '#666666';

  const relatedUnits = units.filter((unit) => unit.id === process.unitId);
  const relatedUnitCount = relatedUnits.length;

  return (
    <div
      className="flex-1 rounded-md border border-zinc-600 px-6 py-4 mr-8 cursor-pointer transition-colors"
      onMouseEnter={() => {
        setHovering(true);
        if (relatedUnits.length > 0) {
          onHoverUnit && onHoverUnit(relatedUnits[0].id);
        }
      }}
      onMouseLeave={() => {
        setHovering(false);
        onHoverUnit && onHoverUnit(null);
      }}
      style={{
        borderColor: borderColor,
        borderWidth: '2px',
        backgroundColor: hovering ? '#222222' : '#1a1a1a',
      }}
    >
      {/* Main content row */}
      <div className="flex min-h-20 flex-row items-center gap-6">
        {/* Process name */}
        <div className="min-w-16">
          <Button
            variant="text"
            className="text-white"
            onClick={() =>
              onSelectUnit
                ? onSelectUnit(relatedUnits[0]?.id || null)
                : dispatch(setSelectedUnit({ apartmentId: relatedUnits[0]?.id }))
            }
            disabled={relatedUnits.length === 0}
          >
            <span className="text-lg font-semibold">{row.data.Process.label}</span>
          </Button>
        </div>

        {/* Status badge */}
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full text-white text-xs font-bold"
          style={{
            border: `2px solid ${borderColor}`,
            backgroundColor: getStatusColor(process.status),
          }}
        >
          {process.status ? process.status.charAt(0).toUpperCase() : '?'}
        </div>

        {/* Duration info */}
        <div className="flex flex-col gap-1 min-w-32">
          <div className="text-sm text-zinc-400">Running Time</div>
          <div className="text-md font-semibold">{row.data['Running Time'].label}</div>
          <div className="text-xs text-zinc-500">{row.data['Relative'].label}</div>
        </div>

        {/* Status info */}
        <div className="flex flex-col gap-1 min-w-24">
          <div className="text-sm text-zinc-400">Status</div>
          <div
            className={`text-md font-semibold ${getStatusTextColor(
              process.status
            )}`}
          >
            {row.data.Status.label}
          </div>
        </div>

        {/* Created date */}
        <div className="flex flex-col gap-1 min-w-24">
          <div className="text-sm text-zinc-400">Created</div>
          <div className="text-sm">{row.data['Created'].label}</div>
        </div>

        {/* Related units */}
        <div className="flex flex-col gap-1 text-center min-w-24">
          <div className="text-xs text-zinc-400">Related Units</div>
          <div className="text-sm font-semibold">{relatedUnitCount}</div>
        </div>

        {/* Right: Urgency indicator */}
        <div className="flex-1 flex flex-col gap-1 text-right">
          <div className="text-xs text-zinc-400">Duration</div>
          <div
            className={`text-md font-semibold ${
              urgency === 'critical'
                ? 'text-red-500'
                : urgency === 'high'
                  ? 'text-orange-500'
                  : urgency === 'medium'
                    ? 'text-yellow-500'
                    : 'text-green-500'
            }`}
          >
            {daysRunning} days
          </div>
        </div>
      </div>

      {/* Related units row (if any) */}
      {relatedUnits.length > 0 && (
        <div className="flex min-h-10 flex-row items-center gap-4 mt-4 pt-4 border-t border-zinc-700">
          <div className="flex-1">
            <div className="text-xs text-zinc-400">Related Unit</div>
            <div className="text-sm font-semibold">{relatedUnits[0].name || relatedUnits[0].id}</div>
          </div>
          {relatedUnits[0].actualRent && (
            <div className="flex flex-col gap-1">
              <div className="text-xs text-zinc-400">Rent</div>
              <div className="text-sm">${relatedUnits[0].actualRent}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Get background color for status badge
 */
function getStatusColor(status: string | undefined): string {
  switch (status) {
    case 'open':
      return '#3b82f6'; // blue
    case 'completed':
      return '#10b981'; // green
    case 'paused':
      return '#f59e0b'; // amber
    default:
      return '#6b7280'; // gray
  }
}

/**
 * Get text color for status
 */
function getStatusTextColor(status: string | undefined): string {
  switch (status) {
    case 'open':
      return 'text-blue-500';
    case 'completed':
      return 'text-green-500';
    case 'paused':
      return 'text-amber-500';
    default:
      return 'text-gray-500';
  }
}
